import { useRef } from 'react';

import { useBreakpoint, VSpacing, GridColumn, GridRow } from '@hh.ru/magritte-ui';

import CatalogLinks from 'src/components/CatalogSearch';
import ExtraFilterCatalogLinks from 'src/components/CatalogSearch/ExtraFilters';
import NotVerifiedEmployerBanner from 'src/components/EmployerVerification/NotVerifiedEmployerBanner';
import {
    useEmployerVerificationState,
    BannerType,
    EmployerVerificationState,
} from 'src/components/EmployerVerification/useEmployerVerificationState';
import AdditionalCheckSnippet from 'src/components/ResumeAdditionalCheck/AdditionalCheckSnippet';
import AbortApplyFiltersContextProvider from 'src/components/Search/Common/Filters/AbortApplyFiltersContext';
import MobileForm from 'src/components/Search/Common/Filters/MobileForm';
import AutoInviteSnippet from 'src/components/Search/Resume/Banners/AutoInvite';
import RegistrationActionForAnonymous from 'src/components/Search/Resume/Banners/RegistrationActionForAnonymous';
import SavedSearchSubscription from 'src/components/Search/Resume/Banners/SavedSearchSubscription';
import ResumeSearchContextBanner from 'src/components/Search/Resume/ContextVacancy/Banner';
import Filters from 'src/components/Search/Resume/Filters';
import SearchResult from 'src/components/Search/Resume/Result';
import Settings from 'src/components/Search/Resume/Settings';
import Pagination from 'src/components/SearchPagination';
import StickySidebar, { StickySidebarAndContent, SCREEN_XS_S } from 'src/components/StickySidebar';
import UnderSearchResultBanner from 'src/components/UnderSearchResultBanner';
import { useSelector } from 'src/hooks/useSelector';
import { ContextViewMode } from 'src/models/resumeSearchContext';
import { UserType } from 'src/models/userType';

const ResumeSearchContent = () => {
    const {
        resumes,
        paging,
        settings: { filtersOnTheRight },
        isClustersEnabled,
        withHiddenResumes,
    } = useSelector((state) => state.resumeSearchResult);
    const resumeSerps = useRef(null);
    const isAnonymous = useSelector(({ userType }) => UserType.Anonymous === userType);
    const catalogLinks = useSelector((state) => state.catalogLinks);
    const shouldHideSettings = withHiddenResumes && resumes.length === 0;
    const showAdditionalCheckBanner = useSelector(({ additionalCheck }) => additionalCheck);

    const { isMobile } = useBreakpoint();

    const {
        isUnverifiedHhRuEmployer,
        shouldShowBanner: showVerificationBanner,
        employerVerificationState,
    } = useEmployerVerificationState();
    const isNewEmployerWithoutDocs = employerVerificationState === EmployerVerificationState.NewWithoutDocs;
    const showResumeSearchContextBanner = useSelector(
        (state) => state.resumeSearchContext?.viewMode === ContextViewMode.Banner
    );
    const showSavedSearchSubscriptionBanner =
        (!showResumeSearchContextBanner || resumes.length === 0) && !shouldHideSettings;
    const newEmployerWithoutDocsClosedCandidatesBanner = useSelector(
        (state) => state.newEmployerWithoutDocsClosedCandidatesBanner
    );
    const isValidForShowingBanner = isNewEmployerWithoutDocs
        ? newEmployerWithoutDocsClosedCandidatesBanner
        : !showVerificationBanner;
    const shouldShowAdditionalCheckBanner = showAdditionalCheckBanner && isValidForShowingBanner;
    const shouldShowSavedSearchSubscriptionBanner = showSavedSearchSubscriptionBanner && isValidForShowingBanner;

    const banners = (
        <>
            {isUnverifiedHhRuEmployer && (
                <>
                    <NotVerifiedEmployerBanner bannerType={BannerType.Candidates} />
                    <VSpacing default={16} />
                </>
            )}
            {(isUnverifiedHhRuEmployer ? shouldShowAdditionalCheckBanner : showAdditionalCheckBanner) && (
                <>
                    <AdditionalCheckSnippet isMagritte dataQa="additional-check-resume-search" />
                    <VSpacing default={16} />
                </>
            )}
            {(isUnverifiedHhRuEmployer
                ? shouldShowSavedSearchSubscriptionBanner
                : showSavedSearchSubscriptionBanner) && <SavedSearchSubscription />}
        </>
    );

    if (!isClustersEnabled) {
        return (
            <div className="resume-serp">
                <GridColumn xs="4" s="8" m="12" l="8">
                    <div>
                        {!shouldHideSettings && (
                            <>
                                <Settings />
                                <VSpacing default={12} />
                            </>
                        )}
                        {banners}
                        <AutoInviteSnippet />
                        <SearchResult />
                        {paging && (
                            <>
                                <VSpacing default={40} gteS={64} />
                                <Pagination paging={paging} />
                            </>
                        )}
                        <UnderSearchResultBanner />
                    </div>
                </GridColumn>
            </div>
        );
    }

    const content = (
        <GridColumn xs="4" s="8" m="9" l="8">
            {isAnonymous && <RegistrationActionForAnonymous />}
            {showResumeSearchContextBanner && <ResumeSearchContextBanner />}
            {!shouldHideSettings && (
                <>
                    <Settings />
                    <VSpacing default={12} />
                </>
            )}
            {banners}
            <AutoInviteSnippet />
            <SearchResult />
            {paging && (
                <>
                    <VSpacing default={40} gteS={64} />
                    <Pagination paging={paging} />
                </>
            )}
            {catalogLinks?.length > 0 && <CatalogLinks queries={catalogLinks} />}
            <ExtraFilterCatalogLinks />
            {resumes.length > 0 && <UnderSearchResultBanner />}
        </GridColumn>
    );

    const showFiltersOnTheRight = !!filtersOnTheRight?.value;

    const filtersWithGrid = (
        <GridColumn xs="0" m="3">
            <Filters />
        </GridColumn>
    );

    return (
        <AbortApplyFiltersContextProvider>
            <div ref={resumeSerps} className="resume-serp">
                <StickySidebarAndContent isGridContent>
                    {!isMobile && !showFiltersOnTheRight && (
                        <StickySidebar>
                            <GridRow>
                                {filtersWithGrid}
                                <GridColumn xs={0} l={1} />
                            </GridRow>
                        </StickySidebar>
                    )}
                    <StickySidebar disabledScreens={SCREEN_XS_S}>{content}</StickySidebar>
                    {!isMobile && showFiltersOnTheRight && (
                        <StickySidebar>
                            <GridRow>
                                <GridColumn xs={0} l={1} />
                                {filtersWithGrid}
                            </GridRow>
                        </StickySidebar>
                    )}
                </StickySidebarAndContent>
                {isMobile && (
                    <MobileForm>
                        <Filters />
                    </MobileForm>
                )}
            </div>
        </AbortApplyFiltersContextProvider>
    );
};

export default ResumeSearchContent;
